import * as React from "react";
import { homeLink, request } from "../../../../../services/cpln";
import { NGProviderBrowser, formatSizeForProviderBrowser } from "../../../ngProviderBrowser";
import { Mk8sInput } from "../../../../../schema/types/mk8s/input/mk8s";
import { Button, Card, Space, Tooltip } from "antd";
import { CardTitle } from "../../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../../components/antd/card/CardContent";
import { v4 as uuidv4 } from "uuid";
import { inputToMk8s } from "../../../draft/utils/inputToMk8s";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../../../gvc/testNewForm/utils";
import { CpInput } from "../../../../gvc/testNewForm/CpInput";
import { CpSelect } from "../../../../gvc/testNewForm/CpSelect";
import { CpFormItem } from "../../../../gvc/testNewForm/CpFormItem";
import { CpErrorWarning } from "../../../../gvc/testNewForm/CpErrorWarning";
import { CpLabel } from "../../../../gvc/testNewForm/CpLabel";
import { CpArrayObject } from "../../../../gvc/testNewForm/CpArrayObject";
import { CpArrayItemNameValuePair } from "../../../../workload/draft/CpArrayItemNameValuePair";
import { CpArrayItemTaint } from "../aws/CpArrayItemTaint";
import { store } from "../../../../../store";
import { useCpFormContext } from "../../../../../components/form/useCpFormContext";
import { CpInputNumber } from "../../../../gvc/testNewForm/CpInputNumber";
import { CpErrorWarningArray } from "../../../../gvc/testNewForm/CpErrorWarningArray";
import { toSelectOptions } from "../../../../../services/defaults";
import { CpErrorWarningArrayIcon } from "../../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { NodePoolCoreButton } from "../nodePoolCoreButton";
import { NodePoolDetectedTags } from "../detectedTags";
import { EllipsisOutlined } from "@ant-design/icons";

interface Props {
  index: number;
}
export const PaperspaceNodePool: React.FC<Props> = ({ index }) => {
  const { id: formId } = useCpFormContext();

  const name = useCpFormValue(`paperspace.nodePools.${index}.name`);
  const machineType = useCpFormValue(`paperspace.nodePools.${index}.machineType`);

  const [browserKey, setBrowserKey] = React.useState("");

  return (
    <>
      <div className="cpln-form-section cpln-ant-tab-content">
        <Card>
          <CardTitle>
            Node Pool{name ? ` | ${name}` : ""}
            <CpErrorWarningArrayIcon names={[`paperspace.nodePools.${index}`]} />
          </CardTitle>
          <CardContent>
            <div>
              <NodePoolDetectedTags name={`paperspace.nodePools.${index}.labels`} />
              <CpFormItem>
                <CpLabel name={`paperspace.nodePools.${index}.name`}>Name</CpLabel>
                <CpInput name={`paperspace.nodePools.${index}.name`} style={{ width: 600 }} />
                <CpErrorWarning name={`paperspace.nodePools.${index}.name`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`paperspace.nodePools.${index}.machineType`}>Machine Type</CpLabel>
                <Space.Compact>
                  <CpInput name={`paperspace.nodePools.${index}.machineType`} style={{ width: 600 }} />
                  <Tooltip title={`Browse`}>
                    <Button icon={<EllipsisOutlined />} onClick={() => setBrowserKey("machine-types")} />
                  </Tooltip>
                </Space.Compact>
                <CpErrorWarning name={`paperspace.nodePools.${index}.machineType`} />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`paperspace.nodePools.${index}.publicIpType`}>Public IP Type</CpLabel>
                <CpSelect
                  name={`paperspace.nodePools.${index}.publicIpType`}
                  options={[
                    { label: "None", value: "none" },
                    { label: "Dynamic", value: "dynamic" },
                    { label: "Static", value: "static" },
                  ]}
                  style={{ width: 600 }}
                />
                <CpErrorWarning name={`paperspace.nodePools.${index}.publicIpType`} />
              </CpFormItem>
              <CpFormItem>
                <div className="flex gap-2">
                  <div className="flex flex-col">
                    <CpLabel name={`paperspace.nodePools.${index}.minSize`}>Min Size</CpLabel>
                    <CpInputNumber name={`paperspace.nodePools.${index}.minSize`} />
                  </div>
                  <div className="flex flex-col">
                    <CpLabel name={`paperspace.nodePools.${index}.maxSize`}>Max Size</CpLabel>
                    <CpInputNumber name={`paperspace.nodePools.${index}.maxSize`} />
                  </div>
                </div>
                <CpErrorWarningArray
                  names={[`paperspace.nodePools.${index}.minSize`, `paperspace.nodePools.${index}.maxSize`]}
                />
              </CpFormItem>
              <CpFormItem>
                <CpLabel name={`paperspace.nodePools.${index}.bootDiskSize`}>Boot Disk Size (GB)</CpLabel>
                <CpSelect
                  name={`paperspace.nodePools.${index}.bootDiskSize`}
                  options={toSelectOptions([50, 100, 250, 500, 1000, 2000])}
                  style={{ width: 180 }}
                />
                <CpErrorWarning name={`paperspace.nodePools.${index}.bootDiskSize`} />
              </CpFormItem>
              <div style={{ maxWidth: 900 }}>
                <CpFormItem>
                  <div className="flex items-center gap-4">
                    <CpLabel name={`skip`}>Labels</CpLabel>
                    <NodePoolCoreButton name={`paperspace.nodePools.${index}.labels`} />
                  </div>
                  <div className="-mt-2">
                    <CpArrayObject
                      factoryFn={() => ({ id: uuidv4(), name: "", value: "" })}
                      labels={["Name", "Value"]}
                      name={`paperspace.nodePools.${index}.labels`}
                    >
                      <CpArrayItemNameValuePair />
                    </CpArrayObject>
                  </div>
                </CpFormItem>
              </div>
              <div style={{ maxWidth: 900 }}>
                <CpArrayObject
                  factoryFn={() => ({ id: uuidv4(), key: "", value: "", effect: "" })}
                  labels={["Key", "Value", "Effect"]}
                  label="Taints"
                  name={`paperspace.nodePools.${index}.taints`}
                >
                  <CpArrayItemTaint />
                </CpArrayObject>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {browserKey === "machine-types" ? (
        <NGProviderBrowser
          // TODO pagination for all discovery endpoints with pages
          title="Browse Paperspace Machine Types"
          initialValue={machineType}
          fetchData={async () => {
            const form = store.getState().form.instances[formId];
            const values: Mk8sInput = form.values as any;

            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "machine-types",
                cluster: inputToMk8s(values),
              },
            });
            return data.data;
          }}
          sortable
          onOk={(value) => {
            formSetValue(formId, `paperspace.nodePools.${index}.machineType`, value as string);
            formTriggerValidation(formId);
          }}
          onClose={() => setBrowserKey("")}
          filteredColumns={["Type"]}
          initialSort={["Cost per Month", "asc"]}
          rightAlign={["Cost per Month"]}
          monoColumns={["Cost per Month"]}
          labels={["Type", "Cost per Month", "Cores", "Memory", "GPU", "GPU Count", "Supports Nv Link"]}
          distinctFilters={["Cores", "Memory", "GPU", "GPU Count", "Supports Nv Link"]}
          getData={(item: any) => {
            const pricePerDay = Number(item.pricePerHour || 0) * 24;
            const pricePerMonth = pricePerDay * 30;
            const cost = pricePerMonth.toLocaleString("en-US", { style: "currency", currency: "USD" });

            return [
              item.machineType,
              item.machineType,
              [pricePerMonth, cost],
              item.cores,
              [item.ram, formatSizeForProviderBrowser(item.ram)],
              item.gpu,
              item.gpuCount,
              [item.supportsNvlink, item.supportsNvlink ? "Yes" : "No"],
            ];
          }}
        />
      ) : null}
    </>
  );
};
